import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import TopNav from '../components/TopNav';
import BottomNav from '../components/BottomNav';
import GradientButton from '../components/GradientButton';
import { colors, radius } from '../theme';

interface Modal {
  type: 'editProfile' | 'changePassword' | 'techPass' | 'notifications' | 'help' | 'report' | null;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, profile, signOut, refreshProfile } = useAuth();
  const [modal, setModal] = useState<Modal>({ type: null });
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const displayName = profile?.displayName || user?.displayName || 'User';
  const email = profile?.email || user?.email || '';
  const phone = profile?.phone || '';
  const plan = profile?.plan || 'free';
  const isTechPass = plan === 'techpass';
  const initial = displayName.charAt(0).toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const upgradeTechPass = async () => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { plan: 'techpass' });
    await refreshProfile();
    setModal({ type: null });
  };

  const cancelTechPass = async () => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), { plan: 'free' });
    await refreshProfile();
    setModal({ type: null });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.background,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        paddingBottom: 80,
      }}
    >
      <TopNav />

      <div style={{ padding: '16px 16px 0' }}>
        <h1 style={{ color: colors.textPrimary, fontSize: 24, fontWeight: 700, margin: '0 0 20px' }}>Profile</h1>

        {/* Profile header card */}
        <div
          style={{
            background: colors.surface,
            borderRadius: 18,
            padding: '20px 18px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'rgba(102,51,153,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              fontWeight: 700,
              color: colors.primary,
              flexShrink: 0,
            }}
          >
            {initial}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 17, marginBottom: 3 }}>
              {displayName}
            </div>
            <div style={{ color: colors.textSecondary, fontSize: 13 }}>{email}</div>
            {phone && (
              <div style={{ color: colors.textSecondary, fontSize: 13, marginTop: 2 }}>{phone}</div>
            )}
          </div>
        </div>

        {/* Tech Pass card */}
        {isTechPass ? (
          <div
            onClick={() => setModal({ type: 'techPass' })}
            style={{
              background: 'rgba(48,209,88,0.1)',
              border: '1px solid rgba(48,209,88,0.25)',
              borderRadius: 16,
              padding: '16px 18px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
          >
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 18 }}>⚡</span>
                <span style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 15 }}>Tech Pass Active</span>
              </div>
              <span style={{ color: colors.textSecondary, fontSize: 12 }}>
                No service fees · Free emergency dispatch · Tap to manage
              </span>
            </div>
            <span style={{ color: colors.success, fontSize: 28 }}>✅</span>
          </div>
        ) : (
          <div
            onClick={() => setModal({ type: 'techPass' })}
            style={{
              background: 'linear-gradient(135deg, #663399, #E040A0)',
              borderRadius: 16,
              padding: '16px 18px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(102,51,153,0.4)',
            }}
          >
            <div>
              <div style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 16, marginBottom: 3 }}>
                Upgrade to Tech Pass
              </div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
                $19.99/mo · No fees · Free ASAP emergency
              </div>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 28 }}>›</span>
          </div>
        )}

        {/* Settings sections */}
        <SettingsSection title="Account">
          <SettingsRow icon="✏️" label="Edit Profile" onClick={() => setModal({ type: 'editProfile' })} />
          <SettingsRow icon="🔒" label="Change Password" onClick={() => setModal({ type: 'changePassword' })} />
          <SettingsRow icon="🎁" label="Refer a Friend — Get a Free Month" onClick={() => {}} />
          <SettingsRow icon="🔔" label="Notifications" onClick={() => setModal({ type: 'notifications' })} />
        </SettingsSection>

        <SettingsSection title="Billing">
          <SettingsRow icon="💳" label="Payment Methods" onClick={() => {}} />
          <SettingsRow icon="📄" label="Billing History" onClick={() => navigate('/my-jobs')} />
          <SettingsRow icon="⭐" label="Manage Tech Pass" onClick={() => setModal({ type: 'techPass' })} />
        </SettingsSection>

        <SettingsSection title="Support">
          <SettingsRow icon="❓" label="Help Center" onClick={() => setModal({ type: 'help' })} />
          <SettingsRow icon="⚠️" label="Report an Issue" onClick={() => setModal({ type: 'report' })} />
          <SettingsRow
            icon="🛡️"
            label="Privacy Policy"
            onClick={() => window.open('https://sagetechapp.com/privacy', '_blank')}
          />
          <SettingsRow
            icon="📃"
            label="Terms of Service"
            onClick={() => window.open('https://sagetechapp.com/terms', '_blank')}
          />
        </SettingsSection>

        <SettingsSection title="Account Actions">
          <SettingsRow
            icon="🗑️"
            label="Delete Account"
            danger
            onClick={() => setShowDeleteConfirm(true)}
          />
        </SettingsSection>

        {/* Sign Out button */}
        <button
          onClick={() => setShowSignOutConfirm(true)}
          style={{
            width: '100%',
            padding: '14px',
            background: 'rgba(255,69,58,0.1)',
            border: 'none',
            borderRadius: 14,
            color: colors.error,
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}
        >
          → Sign Out
        </button>
      </div>

      {/* Sign Out confirm */}
      {showSignOutConfirm && (
        <AlertModal
          title="Sign Out"
          message="Are you sure you want to sign out?"
          confirmLabel="Sign Out"
          danger
          onConfirm={handleSignOut}
          onCancel={() => setShowSignOutConfirm(false)}
        />
      )}

      {/* Delete confirm */}
      {showDeleteConfirm && (
        <AlertModal
          title="Delete Account"
          message="This will permanently delete your account and all associated data. This action cannot be undone."
          confirmLabel="Delete My Account"
          danger
          onConfirm={() => setShowDeleteConfirm(false)}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Tech Pass Modal */}
      {modal.type === 'techPass' && (
        <ModalOverlay onClose={() => setModal({ type: null })}>
          <TechPassModal
            isTechPass={isTechPass}
            onUpgrade={upgradeTechPass}
            onCancel={cancelTechPass}
            onClose={() => setModal({ type: null })}
          />
        </ModalOverlay>
      )}

      {/* Edit Profile Modal */}
      {modal.type === 'editProfile' && (
        <ModalOverlay onClose={() => setModal({ type: null })}>
          <EditProfileModal
            displayName={displayName}
            phone={phone}
            address={profile?.address || ''}
            onSave={async (data) => {
              if (!user) return;
              await updateDoc(doc(db, 'users', user.uid), data);
              await refreshProfile();
              setModal({ type: null });
            }}
            onClose={() => setModal({ type: null })}
          />
        </ModalOverlay>
      )}

      {/* Change Password Modal */}
      {modal.type === 'changePassword' && (
        <ModalOverlay onClose={() => setModal({ type: null })}>
          <ChangePasswordModal onClose={() => setModal({ type: null })} />
        </ModalOverlay>
      )}

      {/* Help Center Modal */}
      {modal.type === 'help' && (
        <ModalOverlay onClose={() => setModal({ type: null })}>
          <HelpCenterModal onClose={() => setModal({ type: null })} />
        </ModalOverlay>
      )}

      {/* Report Modal */}
      {modal.type === 'report' && (
        <ModalOverlay onClose={() => setModal({ type: null })}>
          <ReportModal user={user} onClose={() => setModal({ type: null })} />
        </ModalOverlay>
      )}

      <BottomNav />
    </div>
  );
}

// ─── Settings Section ────────────────────────────────────────────────────────
function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <p
        style={{
          color: colors.textSecondary,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          margin: '0 0 8px 4px',
        }}
      >
        {title}
      </p>
      <div
        style={{
          background: colors.surface,
          borderRadius: 16,
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function SettingsRow({
  icon,
  label,
  danger,
  onClick,
}: {
  icon: string;
  label: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 16px',
        background: 'none',
        border: 'none',
        borderBottom: `1px solid ${colors.divider}`,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{icon}</span>
      <span
        style={{
          flex: 1,
          color: danger ? colors.error : colors.textPrimary,
          fontSize: 15,
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <span style={{ color: colors.textSecondary, fontSize: 16 }}>›</span>
    </button>
  );
}

// ─── Alert Modal ──────────────────────────────────────────────────────────────
function AlertModal({
  title,
  message,
  confirmLabel,
  danger,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        padding: 24,
      }}
    >
      <div
        style={{
          background: colors.surface,
          borderRadius: 20,
          padding: '24px 20px',
          maxWidth: 340,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <h3 style={{ color: colors.textPrimary, fontSize: 18, fontWeight: 700, margin: '0 0 10px' }}>{title}</h3>
        <p style={{ color: colors.textSecondary, fontSize: 14, margin: '0 0 24px' }}>{message}</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '12px',
              background: 'rgba(26,10,46,0.06)',
              border: 'none',
              borderRadius: 12,
              color: colors.textPrimary,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: '12px',
              background: danger ? 'rgba(255,69,58,0.15)' : colors.primary,
              border: 'none',
              borderRadius: 12,
              color: danger ? colors.error : colors.textPrimary,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal Overlay ────────────────────────────────────────────────────────────
function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'flex-end',
        zIndex: 200,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: colors.background,
          borderRadius: '20px 20px 0 0',
          width: '100%',
          maxHeight: '92vh',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Tech Pass Modal ──────────────────────────────────────────────────────────
function TechPassModal({
  isTechPass,
  onUpgrade,
  onCancel,
  onClose,
}: {
  isTechPass: boolean;
  onUpgrade: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);

  const perks = [
    ['⚡', 'No service fees — ever', 'Free plan charges $9.99 per job'],
    ['🚨', 'Free Emergency Service', 'Broadcast to all top-rated techs'],
    ['⭐', 'Priority access to Diamond techs', 'First in line for the best technicians'],
    ['↩️', 'Cancel anytime', 'No contracts, no commitments'],
  ];

  return (
    <div style={{ padding: '24px 20px 40px' }}>
      <div
        style={{
          width: 40,
          height: 4,
          borderRadius: 4,
          background: 'rgba(26,10,46,0.12)',
          margin: '0 auto 20px',
        }}
      />

      {isTechPass ? (
        <>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>⚡</div>
            <h2 style={{ color: colors.textPrimary, fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>
              Tech Pass Active ✓
            </h2>
            <p style={{ color: colors.textSecondary, fontSize: 14, margin: 0 }}>
              You're enjoying all Tech Pass benefits.
            </p>
          </div>
          <button
            onClick={async () => {
              setLoading(true);
              await onCancel();
              setLoading(false);
            }}
            style={{
              width: '100%',
              padding: '14px',
              background: 'rgba(255,69,58,0.1)',
              border: 'none',
              borderRadius: 14,
              color: colors.error,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            }}
          >
            {loading ? 'Cancelling…' : 'Cancel Tech Pass'}
          </button>
          <p style={{ color: colors.textSecondary, fontSize: 12, textAlign: 'center', marginTop: 10 }}>
            You'll keep benefits until the end of your billing period.
          </p>
        </>
      ) : (
        <>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 52, marginBottom: 10 }}>⚡</div>
            <h2 style={{ color: colors.textPrimary, fontSize: 24, fontWeight: 700, margin: '0 0 6px' }}>Tech Pass</h2>
            <div style={{ color: colors.primary, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
              $19.99 / month
            </div>
            <div style={{ color: colors.textSecondary, fontSize: 14 }}>Cancel anytime</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {perks.map(([icon, title, desc]) => (
              <div
                key={title}
                style={{
                  background: colors.surface,
                  borderRadius: 14,
                  padding: '12px 14px',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 22 }}>{icon}</span>
                <div>
                  <div style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{title}</div>
                  <div style={{ color: colors.textSecondary, fontSize: 12 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          <GradientButton
            onClick={async () => {
              setLoading(true);
              await onUpgrade();
              setLoading(false);
            }}
            loading={loading}
            fullWidth
            style={{ fontSize: 16, marginBottom: 12 }}
          >
            Start Tech Pass — $19.99/mo
          </GradientButton>

          <p style={{ color: colors.textSecondary, fontSize: 11, textAlign: 'center', margin: 0 }}>
            Tech Pass is $19.99/month, auto-renewing. Cancel anytime in Settings.
          </p>
        </>
      )}
    </div>
  );
}

// ─── Edit Profile Modal ───────────────────────────────────────────────────────
function EditProfileModal({
  displayName,
  phone,
  address,
  onSave,
  onClose,
}: {
  displayName: string;
  phone: string;
  address: string;
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(displayName);
  const [phoneVal, setPhoneVal] = useState(phone);
  const [addressVal, setAddressVal] = useState(address);
  const [loading, setLoading] = useState(false);

  return (
    <div style={{ padding: '24px 20px 40px' }}>
      <div
        style={{ width: 40, height: 4, borderRadius: 4, background: 'rgba(26,10,46,0.12)', margin: '0 auto 20px' }}
      />
      <h2 style={{ color: colors.textPrimary, fontSize: 20, fontWeight: 700, margin: '0 0 20px', textAlign: 'center' }}>
        Edit Profile
      </h2>

      {[
        { label: 'Display Name', value: name, onChange: setName, type: 'text' },
        { label: 'Phone', value: phoneVal, onChange: setPhoneVal, type: 'tel' },
      ].map(({ label, value, onChange, type }) => (
        <div key={label} style={{ marginBottom: 14 }}>
          <label style={{ color: colors.textSecondary, fontSize: 13, display: 'block', marginBottom: 6 }}>
            {label}
          </label>
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 14px',
              background: 'rgba(26,10,46,0.06)',
              border: '1.5px solid rgba(26,10,46,0.1)',
              borderRadius: 12,
              color: colors.textPrimary,
              fontSize: 15,
              outline: 'none',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
              boxSizing: 'border-box',
            }}
          />
        </div>
      ))}

      <AddressAutocomplete
        value={addressVal}
        onChange={setAddressVal}
        label="Home Address"
        placeholder="Search your home address…"
        style={{ marginBottom: 14 }}
        inputStyle={{ background: 'rgba(26,10,46,0.06)' }}
      />

      <GradientButton
        onClick={async () => {
          setLoading(true);
          await onSave({ displayName: name, phone: phoneVal, address: addressVal });
          setLoading(false);
        }}
        loading={loading}
        fullWidth
        style={{ marginTop: 8 }}
      >
        Save Changes
      </GradientButton>
      <button
        onClick={onClose}
        style={{
          display: 'block',
          width: '100%',
          marginTop: 10,
          padding: '12px',
          background: 'none',
          border: 'none',
          color: colors.textSecondary,
          fontSize: 15,
          cursor: 'pointer',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        }}
      >
        Cancel
      </button>
    </div>
  );
}

// ─── Change Password Modal ────────────────────────────────────────────────────
function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handleChange = async () => {
    if (next !== confirm) { setError('New passwords do not match.'); return; }
    if (next.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const { EmailAuthProvider, reauthenticateWithCredential, updatePassword } = await import('firebase/auth');
      const cred = EmailAuthProvider.credential(user.email!, current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, next);
      setSuccess(true);
    } catch (e: any) {
      setError(e.code === 'auth/wrong-password' ? 'Current password is incorrect.' : 'Password update failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px 20px 40px' }}>
      <div
        style={{ width: 40, height: 4, borderRadius: 4, background: 'rgba(26,10,46,0.12)', margin: '0 auto 20px' }}
      />
      <h2 style={{ color: colors.textPrimary, fontSize: 20, fontWeight: 700, margin: '0 0 20px', textAlign: 'center' }}>
        Change Password
      </h2>
      {success ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <p style={{ color: colors.textPrimary, fontSize: 16 }}>Password updated!</p>
          <GradientButton onClick={onClose} style={{ margin: '16px auto 0' }}>Done</GradientButton>
        </div>
      ) : (
        <>
          {['Current Password', 'New Password', 'Confirm New Password'].map((label, i) => (
            <div key={label} style={{ marginBottom: 14 }}>
              <label style={{ color: colors.textSecondary, fontSize: 13, display: 'block', marginBottom: 6 }}>{label}</label>
              <input
                type="password"
                value={[current, next, confirm][i]}
                onChange={(e) => [setCurrent, setNext, setConfirm][i](e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: 'rgba(26,10,46,0.06)',
                  border: '1.5px solid rgba(26,10,46,0.1)',
                  borderRadius: 12,
                  color: colors.textPrimary,
                  fontSize: 15,
                  outline: 'none',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          ))}
          {error && <p style={{ color: colors.error, fontSize: 13, textAlign: 'center' }}>{error}</p>}
          <GradientButton onClick={handleChange} loading={loading} fullWidth style={{ marginTop: 8 }}>
            Update Password
          </GradientButton>
        </>
      )}
    </div>
  );
}

// ─── Help Center Modal ────────────────────────────────────────────────────────
const FAQS = [
  ['How does SageTech work?', 'SageTech connects you with local, vetted technicians who come to your location. Describe your issue, get matched with an available tech, book them, and track their arrival in real time.'],
  ['How do I book a technician?', "Tap 'Get Help Now' on the home screen, describe your tech issue, browse available technicians, select one, and confirm your booking."],
  ['How does payment work?', 'Your card is held (not charged) when you book. After the job is completed, you\'re charged based on the actual time spent.'],
  ['How do I cancel?', "Cancel from the Job Status screen before the technician arrives."],
  ['What if the tech can\'t fix it?', "If your technician can't fix the issue, your card hold is released — you're not charged."],
];

function HelpCenterModal({ onClose }: { onClose: () => void }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  return (
    <div style={{ padding: '24px 16px 40px' }}>
      <div
        style={{ width: 40, height: 4, borderRadius: 4, background: 'rgba(26,10,46,0.12)', margin: '0 auto 20px' }}
      />
      <h2 style={{ color: colors.textPrimary, fontSize: 20, fontWeight: 700, margin: '0 0 16px', textAlign: 'center' }}>
        Help Center
      </h2>
      {FAQS.map(([q, a], i) => (
        <div
          key={i}
          style={{
            background: colors.surface,
            borderRadius: 14,
            marginBottom: 10,
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => setExpanded(expanded === i ? null : i)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 16px',
              background: 'none',
              border: 'none',
              color: colors.textPrimary,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            }}
          >
            <span style={{ flex: 1, paddingRight: 10 }}>{q}</span>
            <span style={{ color: colors.textSecondary, fontSize: 18 }}>{expanded === i ? '∧' : '∨'}</span>
          </button>
          {expanded === i && (
            <div style={{ padding: '0 16px 14px', color: colors.textSecondary, fontSize: 14, lineHeight: 1.6 }}>
              {a}
            </div>
          )}
        </div>
      ))}
      <button
        onClick={onClose}
        style={{
          display: 'block',
          width: '100%',
          padding: '12px',
          background: 'rgba(26,10,46,0.06)',
          border: 'none',
          borderRadius: 12,
          color: colors.textPrimary,
          fontSize: 15,
          cursor: 'pointer',
          marginTop: 8,
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        }}
      >
        Close
      </button>
    </div>
  );
}

// ─── Report Modal ─────────────────────────────────────────────────────────────
function ReportModal({ user, onClose }: { user: any; onClose: () => void }) {
  const [category, setCategory] = useState('App Bug');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    try {
      const { addDoc, collection, serverTimestamp: st, Timestamp } = await import('firebase/firestore');
      await addDoc(collection(db, 'reports'), {
        userId: user?.uid || '',
        category,
        description: description.trim(),
        timestamp: st(),
        status: 'open',
      });
      setSuccess(true);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '24px 20px 40px' }}>
      <div
        style={{ width: 40, height: 4, borderRadius: 4, background: 'rgba(26,10,46,0.12)', margin: '0 auto 20px' }}
      />
      <h2 style={{ color: colors.textPrimary, fontSize: 20, fontWeight: 700, margin: '0 0 20px', textAlign: 'center' }}>
        Report an Issue
      </h2>
      {success ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <p style={{ color: colors.textPrimary, fontSize: 16 }}>Report submitted. We'll review it shortly.</p>
          <GradientButton onClick={onClose} style={{ margin: '16px auto 0' }}>Done</GradientButton>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 14 }}>
            <label style={{ color: colors.textSecondary, fontSize: 13, display: 'block', marginBottom: 8 }}>
              Category
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['App Bug', 'Technician Issue', 'Payment Issue', 'Other'].map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  style={{
                    padding: '7px 14px',
                    background: category === c ? colors.primary : 'rgba(26,10,46,0.06)',
                    border: 'none',
                    borderRadius: 20,
                    color: colors.textPrimary,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: colors.textSecondary, fontSize: 13, display: 'block', marginBottom: 6 }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe the issue in as much detail as possible…"
              rows={5}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: 'rgba(255,255,255,0.05)',
                border: '1.5px solid rgba(26,10,46,0.1)',
                borderRadius: 12,
                color: colors.textPrimary,
                fontSize: 15,
                outline: 'none',
                resize: 'none',
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <GradientButton
            onClick={submit}
            disabled={!description.trim()}
            loading={submitting}
            fullWidth
          >
            Submit Report
          </GradientButton>
        </>
      )}
    </div>
  );
}
